import React, { useState } from "react";
import WhiteButton from "../../../../Components/WhiteButton.js";
import { ArrowRight } from "lucide-react";
import Toast from "../../../../Components/Toast.js";

const detailList = [
  {
    title: "Email Us Here",
    description: "contact@datapixl.ai",
    href: "mailto:contact@datapixl.ai",
  },
  {
    title: "Meet Us Here",
    description:
      "HD-077, WeWork Embassy TechVillage, Block L, Devarabisanahalli, Outer Ring Rd, Bellandur, Bengaluru, Karnataka 560103",
  },
];

const tags = [
  "Data and AI Strategy",
  "Generative AI",
  "AI Center of Excellence",
  "Advanced Analytics and Machine Learning",
  "Others",
];

const ContactUsInitialSection = () => {
  const [showToast, setShowToast] = useState(false);
  const [timeoutId, setTimeoutId] = useState(null);

  const triggerToast = () => {
    setShowToast(true);

    // Clear any existing timeout
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    // Set a new timeout
    const newTimeout = setTimeout(() => {
      setShowToast(false);
      setTimeoutId(null);
    }, 3000);

    setTimeoutId(newTimeout);
  };

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });
  const [selected, setSelected] = useState("");
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "phone") {
      let formatted = value;

      // Allow only digits and one optional + at start
      if (formatted.startsWith("+")) {
        // Remove non-digits except leading +
        formatted = "+" + formatted.slice(1).replace(/\D/g, "");
      } else {
        // Only digits
        formatted = formatted.replace(/\D/g, "");
      }

      setFormData((prev) => ({ ...prev, [name]: formatted }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";

    if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.email))
      newErrors.email = "Invalid email";

    if (!/^\+?\d{10,15}$/.test(formData.phone))
      newErrors.phone = "Enter a valid phone number (10–15 digits)";

    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length === 0) {
      fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          "form-name": "contact",
          ...formData,
          tag: selected,
        }).toString(),
      });

      triggerToast();
      // ✅ Clear form
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        message: "",
      });
      setSelected("");
      setErrors({});
    }
  };

  return (
    <section className="relative mb-12">
      <div className="flex flex-col p-1 md:p-4 min-h-screen">
        <div className="text-white flex-1 flex flex-col">
          <main className="flex flex-col items-center pt-[16%] md:pt-[6%] mx-[6%] md:mx-[32%] z-10 pb-[8%]">
            <h1 className="text-4xl md:text-7xl font-extrabold text-center text-white leading-[140%] tracking-[0] mb-6">
              Let’s Connect
            </h1>

            <p className="text-center text-lg md:text-xl font-medium leading-[140%] tracking-[0] md:mx-[5%] text-white">
              Have questions? <br /> Reach out, and let's start the
              conversation.
            </p>
          </main>
          <div className="p-3 md:px-16 flex flex-col md:flex-row md:space-x-28 items-start justify-center">
            <div className="group relative p-[1px] bg-contact-us-border rounded-2xl md:min-w-[50%]">
              <form
                onSubmit={handleSubmit}
                name="contact"
                method="POST"
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                netlify
                className="relative flex flex-col items-start md:gap-2 bg-app-background rounded-2xl px-2 pt-8 md:px-16 md:py-10"
              >
                <div className="space-y-8 md:space-y-6">
                  {/* Hidden input to tell Netlify the form name */}
                  <input type="hidden" name="form-name" value="contact" />

                  <Input
                    name="name"
                    placeholder="Name"
                    value={formData.name}
                    onChange={handleChange}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm">{errors.name}</p>
                  )}

                  <Input
                    name="email"
                    type="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm">{errors.email}</p>
                  )}

                  <Input
                    name="phone"
                    type="tel"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleChange}
                    maxLength={15}
                  />
                  {errors.phone && (
                    <p className="text-red-500 text-sm">{errors.phone}</p>
                  )}

                  <Input
                    name="company"
                    placeholder="Company Name"
                    value={formData.company}
                    onChange={handleChange}
                  />
                </div>
                {/* Tag selector */}
                <div className="space-y-4 py-8 md:py-14">
                  <p className="font-medium text-[16px] leading-[100%] tracking-[0%]">
                    What do you need help with?
                  </p>
                  <div className="flex flex-wrap gap-4">
                    {tags.map((tag, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => setSelected(tag)}
                        className={`font-medium text-[12px] md:text-[16px] leading-[140%] tracking-[0%] border rounded-full px-5 md:px-6 py-4 transition-all ${
                          selected === tag
                            ? "bg-white text-black"
                            : "border-white text-white hover:bg-white hover:text-black duration-300"
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Textarea */}
                <textarea
                  name="message"
                  placeholder="Something we need to know?"
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-white placeholder-[#AFAFAF] outline-none resize-none h-24 text-[16px] md:text-[20px] leading-[100%] tracking-[0%] mb-8 md:mb-14"
                />

                <div className="mb-12 md:mb-0">
                  <WhiteButton
                    text="Send Message"
                    icon={<ArrowRight />}
                    type="submit"
                    onClick={() => {}}
                  />
                </div>
              </form>
            </div>

            <div className="mt-[30%] md:mt-0 space-y-10 md:space-y-20">
              {detailList.map((detail, index) => (
                <div className="flex flex-col">
                  <h1 className="font-bold text-[20px] md:text-[24px] leading-[140%] tracking-[0%]">
                    {detail.title}
                  </h1>
                  {detail.href ? (
                    <a
                      className={`font-normal text-[16px] md:text-[20px] leading-[140%] tracking-[0%] mt-2 md:mt-0 underline decoration-solid decoration-[6.5%] underline-offset-[24%] [text-decoration-skip-ink:auto]`}
                      href={detail.href}
                    >
                      {detail.description}
                    </a>
                  ) : (
                    <p
                      className={`font-normal text-[16px] md:text-[20px] leading-[140%] tracking-[0%] mt-2 md:mt-0`}
                    >
                      {detail.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Toast
        show={showToast}
        message="Thanks for reaching out! We’ll get back to you soon."
        onClose={() => {
          setShowToast(false);
          if (timeoutId) clearTimeout(timeoutId);
        }}
      />
    </section>
  );
};

function Input({
  value,
  onChange,
  placeholder,
  type = "text",
  name,
  maxLength,
}) {
  return (
    <input
      type={type}
      name={name}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      maxLength={maxLength}
      className="w-full bg-transparent border-b text-white border-white placeholder-[#AFAFAF] outline-none font-normal text-[16px] md:text-[20px] leading-[100%] tracking-[0%] py-2"
    />
  );
}

export default ContactUsInitialSection;
